/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState } from 'react';
import moment from 'moment';
import {
  IconButton, Typography, Button, CircularProgress, Box, TextField, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import type { QueryKey } from 'react-query';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import type { LeaseDetail, LeaseListItem, DocumentType } from './types';
import DocumentCard from './DocumentCard';
import UploadDialog from './UploadDialog';

interface LeaseDetailSideSheetProps {
  leaseId: number;
  workspaceId: number;
  isClosing: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
}

interface PropertyOption {
  id: number;
  label: string;
}

/** Cached axios response of the Leases table query (['get/leases', workspaceId, search]) */
interface LeasesListResponse {
  data: { data: LeaseListItem[] };
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  return moment(dateStr).format('MMMM D, YYYY');
};

export default function LeaseDetailSideSheet({
  leaseId,
  workspaceId,
  isClosing,
  onClose,
  onRefresh,
}: LeaseDetailSideSheetProps): JSX.Element {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [amendmentDialogOpen, setAmendmentDialogOpen] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);

  // Inline editing for tenant name
  const [isEditingTenantName, setIsEditingTenantName] = useState(false);
  const [editTenantNameValue, setEditTenantNameValue] = useState('');

  // Inline editing for unit/suite
  const [isEditingUnitSuite, setIsEditingUnitSuite] = useState(false);
  const [editUnitSuiteValue, setEditUnitSuiteValue] = useState('');

  // Inline editing for building/property
  const [isEditingBuilding, setIsEditingBuilding] = useState(false);

  // Amendment document awaiting "Detach" confirmation
  const [detachDocId, setDetachDocId] = useState<number | null>(null);

  // Fetch lease detail
  const { data: lease, isLoading } = useQuery(
    ['get/leases', leaseId],
    async () => axios({
      url: `api/leases/${leaseId}/`,
      method: 'get',
    }),
    {
      enabled: Boolean(leaseId),
      select: (res: AxiosResponse) => res.data.data as LeaseDetail,
      refetchInterval: (data) => {
        // A lease held at the daily upload limit isn't being processed, so don't keep polling it.
        const anyProcessing = !data?.processing_status
          && (data?.is_processing || data?.documents?.some((d) => !d.is_processed));
        return anyProcessing ? 5000 : false;
      },
    },
  );

  // Add amendment mutation
  const { mutate: addAmendment, isLoading: isAddingAmendment } = useMutation(
    async (file: File) => {
      const formData = new FormData();
      formData.append('pdf', file);
      return axios({
        url: `api/leases/${leaseId}/add_amendment/`,
        method: 'post',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        await onRefresh();
        enqueueSnackbar('Amendment uploaded and processing started');
      },
      onError: () => {
        enqueueSnackbar('Failed to upload amendment', { variant: 'error' });
      },
    },
  );

  // Replace lease mutation
  const { mutate: replaceLease, isLoading: isReplacing } = useMutation(
    async (file: File) => {
      const formData = new FormData();
      formData.append('pdf', file);
      return axios({
        url: `api/leases/${leaseId}/replace_lease/`,
        method: 'post',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        await onRefresh();
        enqueueSnackbar('Base lease replaced and re-processing started');
      },
      onError: () => {
        enqueueSnackbar('Failed to replace lease', { variant: 'error' });
      },
    },
  );

  // Update tenant name mutation
  const { mutate: updateTenantName, isLoading: isUpdatingTenantName } = useMutation(
    async ({ id, tenantName }: { id: number; tenantName: string }) => axios({
      url: `api/leases/${id}/`,
      method: 'patch',
      data: { tenant_name: tenantName },
    }),
    {
      onSuccess: () => {
        // Close the editor and confirm immediately so the field doesn't appear
        // to hang while refetches complete. Invalidations run in the background
        // (not awaited) — the table preserves the user's sort across the update.
        setIsEditingTenantName(false);
        enqueueSnackbar('Tenant name updated');
        queryClient.invalidateQueries(['get/leases', leaseId]).catch(() => {});
        queryClient.invalidateQueries('get/leases').catch(() => {});
        queryClient.invalidateQueries('get/options-monitor').catch(() => {});
      },
      onError: () => {
        enqueueSnackbar('Failed to update tenant name', { variant: 'error' });
        setIsEditingTenantName(false);
      },
    },
  );

  // Update unit/suite mutation
  const { mutate: updateUnitSuite, isLoading: isUpdatingUnitSuite } = useMutation(
    async ({ id, unitSuite }: { id: number; unitSuite: string }) => axios({
      url: `api/leases/${id}/`,
      method: 'patch',
      data: { unit_suite: unitSuite },
    }),
    {
      onSuccess: () => {
        // Close the editor and confirm immediately; refetch in the background.
        setIsEditingUnitSuite(false);
        enqueueSnackbar('Suite updated');
        queryClient.invalidateQueries(['get/leases', leaseId]).catch(() => {});
        queryClient.invalidateQueries('get/leases').catch(() => {});
        queryClient.invalidateQueries('get/options-monitor').catch(() => {});
      },
      onError: () => {
        enqueueSnackbar('Failed to update suite', { variant: 'error' });
        setIsEditingUnitSuite(false);
      },
    },
  );

  // Fetch buildings/properties for the current workspace (for the selector)
  const { data: propertyOptions = [] } = useQuery(
    ['get/lease-building-options', workspaceId],
    async () => axios({
      url: `api/filter/property/?workspace=${workspaceId}`,
      method: 'get',
    }),
    {
      enabled: Boolean(workspaceId),
      select: (
        res: AxiosResponse<{ detail?: { id: number; address: string; name: string }[] }>,
      ): PropertyOption[] => (res.data.detail || []).map(
        (p) => ({
          id: p.id,
          label: p.address || p.name || `Property #${p.id}`,
        }),
      ),
    },
  );

  // Update building/property mutation
  const { mutate: updateBuilding, isLoading: isUpdatingBuilding } = useMutation(
    async ({ id, propertyId }: { id: number; propertyId: number | null }) => axios({
      url: `api/leases/${id}/`,
      method: 'patch',
      data: { property_id: propertyId },
    }),
    {
      onSuccess: () => {
        setIsEditingBuilding(false);
        enqueueSnackbar('Building updated');
        queryClient.invalidateQueries(['get/leases', leaseId]).catch(() => {});
        queryClient.invalidateQueries('get/leases').catch(() => {});
        queryClient.invalidateQueries('get/options-monitor').catch(() => {});
      },
      onError: () => {
        enqueueSnackbar('Failed to update building', { variant: 'error' });
        setIsEditingBuilding(false);
      },
    },
  );

  // Update document type mutation
  const { mutate: updateDocType } = useMutation(
    async ({ documentId, documentType }: { documentId: number; documentType: DocumentType }) => axios({
      url: `api/leases/${leaseId}/update_document/`,
      method: 'patch',
      data: { document_id: documentId, document_type: documentType },
    }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        enqueueSnackbar('Document updated');
      },
      onError: () => {
        enqueueSnackbar('Failed to update document', { variant: 'error' });
      },
    },
  );

  // Remove document mutation
  const { mutate: removeDocument } = useMutation(
    async (documentId: number) => axios({
      url: `api/leases/${leaseId}/remove_document/`,
      method: 'delete',
      data: { document_id: documentId },
    }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        enqueueSnackbar('Document removed');
      },
      onError: () => {
        enqueueSnackbar('Failed to remove document', { variant: 'error' });
      },
    },
  );

  // Amendment suggestion: attach this upload to the suggested lease (this lease is then removed).
  // Optimistic: close the panel and update the Leases table at once (this upload's row goes, the
  // target row shows "Processing...") instead of waiting for the request and the list refetch.
  const { mutate: attachAsAmendment, isLoading: isAttaching } = useMutation(
    async (targetLeaseId: number) => axios({
      url: `api/leases/${leaseId}/attach_as_amendment/`,
      method: 'post',
      data: { target_lease_id: targetLeaseId },
    }),
    {
      onMutate: async (targetLeaseId: number) => {
        const isLeasesList = (query: { queryKey: unknown }): boolean => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === 'get/leases' && key.length === 3;
        };
        await queryClient.cancelQueries({ predicate: isLeasesList });
        const snapshot = queryClient.getQueriesData<LeasesListResponse>({ predicate: isLeasesList });
        queryClient.setQueriesData<LeasesListResponse | undefined>({ predicate: isLeasesList }, (old) => {
          if (!old) return old;
          const rows = old.data.data
            .filter((row) => row.id !== leaseId)
            .map((row) => {
              if (row.id === targetLeaseId) return { ...row, is_processing: true };
              return row;
            });
          return { ...old, data: { ...old.data, data: rows } };
        });
        enqueueSnackbar('Attaching amendment...');
        onClose();
        return { snapshot };
      },
      onSuccess: () => {
        enqueueSnackbar('Amendment attached — processing it now');
      },
      onError: (_error: unknown, _targetLeaseId: number, context?: { snapshot: [QueryKey, LeasesListResponse | undefined][] }) => {
        context?.snapshot.forEach(([key, data]) => { queryClient.setQueryData(key, data); });
        enqueueSnackbar('Failed to attach as an amendment', { variant: 'error' });
      },
      onSettled: () => {
        queryClient.invalidateQueries('get/leases').catch(() => {});
        queryClient.invalidateQueries('get/options-monitor').catch(() => {});
      },
    },
  );

  // Amendment suggestion: "not an amendment"
  const { mutate: dismissSuggestion, isLoading: isDismissing } = useMutation(
    async () => axios({
      url: `api/leases/${leaseId}/dismiss_suggestion/`,
      method: 'post',
    }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        queryClient.invalidateQueries('get/leases').catch(() => {});
      },
      onError: () => {
        enqueueSnackbar('Failed to dismiss the suggestion', { variant: 'error' });
      },
    },
  );

  // Detach an amendment into its own lease; both leases are re-processed
  const { mutate: detachDocument, isLoading: isDetaching } = useMutation(
    async (documentId: number) => axios({
      url: `api/leases/${leaseId}/detach_document/`,
      method: 'post',
      data: { document_id: documentId },
    }),
    {
      onSuccess: async () => {
        setDetachDocId(null);
        enqueueSnackbar('Amendment detached into its own lease — both leases are being re-processed');
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        queryClient.invalidateQueries('get/leases').catch(() => {});
        queryClient.invalidateQueries('get/options-monitor').catch(() => {});
      },
      onError: () => {
        setDetachDocId(null);
        enqueueSnackbar('Failed to detach the amendment', { variant: 'error' });
      },
    },
  );

  const suggestion = lease?.amendment_suggestion ?? null;

  return (
    <div className={`leases-detail-sidesheet${isClosing ? ' closing' : ''}`}>
      {/* Header */}
      <div className='sidesheet-header'>
        <div className='header-top-row'>
          <div style={{ flex: 1 }}>
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                {isEditingTenantName ? (
                  <Box display='flex' alignItems='center' gap={0.5} mb={0.5}>
                    <TextField
                      size='small'
                      value={editTenantNameValue}
                      onChange={(e): void => { setEditTenantNameValue(e.target.value); }}
                      onKeyDown={(e): void => {
                        if (e.key === 'Enter') {
                          updateTenantName({ id: leaseId, tenantName: editTenantNameValue });
                        } else if (e.key === 'Escape') {
                          setIsEditingTenantName(false);
                        }
                      }}
                      autoFocus
                      placeholder='Enter tenant name'
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '16px', fontWeight: 600, padding: '4px 8px' },
                        '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                      }}
                    />
                    <IconButton
                      size='small'
                      onClick={(): void => { updateTenantName({ id: leaseId, tenantName: editTenantNameValue }); }}
                      disabled={isUpdatingTenantName}
                      sx={{ color: '#00CFA1' }}
                    >
                      <CheckIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={(): void => { setIsEditingTenantName(false); }}
                      sx={{ color: '#6B7280' }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <h2
                    className='header-title'
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                    onClick={(): void => {
                      setIsEditingTenantName(true);
                      setEditTenantNameValue(lease?.tenant_name || '');
                    }}
                    aria-hidden='true'
                  >
                    {lease?.tenant_name || 'Unnamed Tenant'}
                    <EditIcon sx={{ fontSize: 14, color: '#9CA3AF', opacity: 0.6 }} />
                  </h2>
                )}
                <p className='header-subtitle'>{lease?.building_name || ''}</p>
                <p className='header-agreement'>{lease?.lease_agreement_date || ''}</p>
              </>
            )}
          </div>
          {!isEditingTenantName && (
            <IconButton onClick={onClose} size='small'>
              <ClearIcon />
            </IconButton>
          )}
        </div>
      </div>

      {/* Content */}
      <div className='sidesheet-content'>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <CircularProgress />
          </div>
        ) : lease ? (
          <>
            {suggestion && (
              <Box
                sx={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                  backgroundColor: '#EBFBF7',
                  border: '1px solid #00CFA1',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  marginBottom: '16px',
                }}
              >
                <InfoOutlinedIcon sx={{ color: '#00CFA1', fontSize: 20, marginTop: '1px' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '13px', color: '#374151' }}>
                    This upload looks like an amendment for <strong>{suggestion.target_tenant}</strong>
                    {' '}({suggestion.reason.toLowerCase()}).
                  </Typography>
                  <Box display='flex' gap={1} marginTop='10px'>
                    <Button
                      variant='contained'
                      size='small'
                      disabled={isAttaching || isDismissing}
                      onClick={(): void => { attachAsAmendment(suggestion.target_lease_id); }}
                      sx={{
                        backgroundColor: '#00CFA1',
                        color: '#fff',
                        textTransform: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        padding: '4px 14px',
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#00B890', boxShadow: 'none' },
                      }}
                    >
                      Attach as amendment
                    </Button>
                    <Button
                      variant='outlined'
                      size='small'
                      disabled={isAttaching || isDismissing}
                      onClick={(): void => { dismissSuggestion(); }}
                      sx={{
                        textTransform: 'none',
                        fontSize: '13px',
                        color: '#374151',
                        borderColor: '#D1D5DB',
                        borderRadius: '6px',
                        padding: '4px 14px',
                        backgroundColor: '#fff',
                        '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' },
                      }}
                    >
                      Not an amendment
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Info Grid */}
            <div className='info-grid'>
              <div className='info-item'>
                <span className='info-label'>Current Expiration</span>
                <span className='info-value'>{formatDate(lease.lease_expiration_date)}</span>
                {lease.expiration_source && (
                  <span className='info-source'>
                    Derived from: {lease.expiration_source}
                  </span>
                )}
              </div>
              <div className='info-item'>
                <span className='info-label'>Amendments</span>
                <span className='info-value'>{lease.amendment_count}</span>
              </div>
              <div className='info-item'>
                <span className='info-label'>Building</span>
                {isEditingBuilding ? (
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <Autocomplete<PropertyOption>
                      size='small'
                      options={propertyOptions}
                      value={propertyOptions.find((o) => o.id === lease.property_id) || null}
                      getOptionLabel={(o): string => o.label}
                      isOptionEqualToValue={(o, v): boolean => o.id === v.id}
                      disabled={isUpdatingBuilding}
                      openOnFocus
                      onChange={(_e, newValue): void => {
                        updateBuilding({ id: leaseId, propertyId: newValue ? newValue.id : null });
                      }}
                      renderInput={(params): JSX.Element => (
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        <TextField {...params} autoFocus placeholder='Select building' />
                      )}
                      sx={{
                        flex: 1,
                        minWidth: 180,
                        '& .MuiInputBase-input': { fontSize: '13px', padding: '2px 4px' },
                        '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                      }}
                    />
                    <IconButton
                      size='small'
                      onClick={(): void => { setIsEditingBuilding(false); }}
                      sx={{ color: '#6B7280' }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <span
                    className='info-value'
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onClick={(): void => { setIsEditingBuilding(true); }}
                    aria-hidden='true'
                  >
                    {lease.building_name || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Unassigned</span>}
                    <EditIcon sx={{ fontSize: 14, color: '#9CA3AF', opacity: 0.6 }} />
                  </span>
                )}
              </div>
              <div className='info-item'>
                <span className='info-label'>Suite</span>
                {isEditingUnitSuite ? (
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <TextField
                      size='small'
                      value={editUnitSuiteValue}
                      onChange={(e): void => { setEditUnitSuiteValue(e.target.value); }}
                      onKeyDown={(e): void => {
                        if (e.key === 'Enter') {
                          updateUnitSuite({ id: leaseId, unitSuite: editUnitSuiteValue });
                        } else if (e.key === 'Escape') {
                          setIsEditingUnitSuite(false);
                        }
                      }}
                      onBlur={(): void => { updateUnitSuite({ id: leaseId, unitSuite: editUnitSuiteValue }); }}
                      autoFocus
                      placeholder='Enter suite'
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '13px', padding: '4px 8px' },
                        '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                      }}
                    />
                    <IconButton
                      size='small'
                      onClick={(): void => { updateUnitSuite({ id: leaseId, unitSuite: editUnitSuiteValue }); }}
                      disabled={isUpdatingUnitSuite}
                      sx={{ color: '#00CFA1' }}
                    >
                      <CheckIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={(): void => { setIsEditingUnitSuite(false); }}
                      sx={{ color: '#6B7280' }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <span
                    className='info-value'
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onClick={(): void => { setIsEditingUnitSuite(true); setEditUnitSuiteValue(lease.unit_suite || ''); }}
                    aria-hidden='true'
                  >
                    {lease.unit_suite || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Click to add</span>}
                    <EditIcon sx={{ fontSize: 14, color: '#9CA3AF', opacity: 0.6 }} />
                  </span>
                )}
              </div>
              <div className='info-item'>
                <span className='info-label'>Has Options</span>
                <span className='info-value'>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: lease.has_options ? '#E8F5E9' : '#F3F4F6',
                      color: lease.has_options ? '#2E7D32' : '#6B7280',
                    }}
                  >
                    {lease.has_options ? 'Yes' : 'No'}
                  </span>
                </span>
              </div>
            </div>

            {/* Documents Section */}
            <div className='documents-section'>
              <div className='section-header'>
                <span className='section-title'>Documents</span>
              </div>

              {lease.documents?.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  leaseId={leaseId}
                  isUploading={isReplacing && doc.document_type === 'base_lease'}
                  onTypeChange={(documentId, newType) => {
                    updateDocType({ documentId, documentType: newType });
                  }}
                  onDelete={(documentId) => {
                    removeDocument(documentId);
                  }}
                  canDelete={(lease.documents?.length || 0) > 1}
                  onDetach={(documentId) => {
                    setDetachDocId(documentId);
                  }}
                />
              ))}

              {isAddingAmendment && (
                <div
                  style={{
                    position: 'relative',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '8px',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <CircularProgress size={16} sx={{ color: '#00CFA1', flexShrink: 0 }} />
                  <div>
                    <span
                      style={{
                        backgroundColor: '#F0FDF4',
                        color: '#15803D',
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        display: 'inline-block',
                        marginBottom: '3px',
                      }}
                    >
                      Amendment
                    </span>
                    <Typography variant='body2' style={{ color: '#9CA3AF', fontSize: '12px' }}>
                      Uploading...
                    </Typography>
                  </div>
                </div>
              )}

              {/* Action buttons below documents */}
              <div className='documents-actions'>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={(): void => { setAmendmentDialogOpen(true); }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    color: '#374151',
                    borderColor: '#D1D5DB',
                    borderRadius: '6px',
                    padding: '6px 14px',
                    '&:hover': {
                      borderColor: '#9CA3AF',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  Add Amendment
                </Button>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<SwapHorizIcon />}
                  onClick={(): void => { setReplaceDialogOpen(true); }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    color: '#374151',
                    borderColor: '#D1D5DB',
                    borderRadius: '6px',
                    padding: '6px 14px',
                    '&:hover': {
                      borderColor: '#9CA3AF',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  Replace Lease
                </Button>
                {(lease.documents?.length || 0) > 1 && (
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={(): void => {
                      window.location.href = `${process.env.REACT_APP_BASE_URL}/api/leases/${leaseId}/download_all_documents/`;
                    }}
                    sx={{
                      textTransform: 'none',
                      fontSize: '13px',
                      color: '#374151',
                      borderColor: '#D1D5DB',
                      borderRadius: '6px',
                      padding: '6px 14px',
                      '&:hover': {
                        borderColor: '#9CA3AF',
                        backgroundColor: '#F9FAFB',
                      },
                    }}
                  >
                    Combine &amp; Download
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <Typography style={{ color: '#9CA3AF', textAlign: 'center', padding: '40px' }}>
            Lease not found
          </Typography>
        )}
      </div>

      {/* Detach confirmation */}
      <Dialog open={detachDocId !== null} onClose={(): void => { setDetachDocId(null); }}>
        <DialogTitle>Detach this amendment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The amendment will become its own lease, and this lease will be re-processed without it
            (options, dates, suite and square footage are rebuilt).
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 16px' }}>
          <Button
            variant='outlined'
            disabled={isDetaching}
            onClick={(): void => { setDetachDocId(null); }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#374151',
              borderColor: '#D1D5DB',
              borderRadius: '6px',
              '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            disabled={isDetaching}
            onClick={(): void => { if (detachDocId !== null) detachDocument(detachDocId); }}
            sx={{
              backgroundColor: '#DC2626',
              color: '#fff',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#B91C1C', boxShadow: 'none' },
            }}
          >
            Detach
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialogs */}
      <UploadDialog
        open={amendmentDialogOpen}
        onClose={(): void => { setAmendmentDialogOpen(false); }}
        onUpload={(file: File): void => { addAmendment(file); }}
        title='Upload Amendment'
        subtitle='This file will be classified as an Amendment.'
      />

      <UploadDialog
        open={replaceDialogOpen}
        onClose={(): void => { setReplaceDialogOpen(false); }}
        onUpload={(file: File): void => { replaceLease(file); }}
        title='Replace Base Lease'
        subtitle='This will replace the existing base lease document and re-process the entire lease.'
      />
    </div>
  );
}
